/**
 * New activity (1.5.1, Event & activity support): one question per screen — what → when → where and how many →
 * check and save. Volunteer help with no tenant: the piano on Saturdays, bingo-night support, setup, prep, cooler
 * duty. Weekends are open. The standing note "Check in with ABF staff at the front desk" goes with every request.
 * Saving sends nothing: the office presses Send on a date (Events → Event & activity support).
 */
import { SpanishWording, useSpanishPeople } from '@/features/messages/SpanishWording';
import { useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { addDaysToKey, dayOfWeek } from '@shared/dates';
import { lengthWords } from '@shared/dayplan';
import { ACTIVITY_CAP_MAX, type ActivityInput, type ActivityView } from '@shared/activities';
import { SERVICE_KEYS, minutesLabel, type ServiceKey } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StepHead, SummaryCard } from '@/components/Steps';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { SelectField, TextArea, TextField } from '@/features/people/fields';
import { formatDayMedium } from '@/lib/dates';
import { postJson, useActivityRefresh } from './api';

const STEPS = ['what', 'when', 'where', 'confirm'] as const;
type Step = (typeof STEPS)[number];
const TIMES = Array.from({ length: 31 }, (_, i) => 7 * 60 + i * 30); // 7:00 am – 10:00 pm
const LENGTHS = Array.from({ length: 16 }, (_, i) => 30 + i * 30); // 30 min – 8 h
const iso = (k: string) => `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}`;

export function weekdayName(weekday: number, lang: string): string {
  // 2026-10-04 is a Sunday
  return new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 4 + weekday, 12)));
}

export function NewActivitySheet({ onClose, returnFocusTo, todayKey }: { onClose: () => void; returnFocusTo: RefObject<HTMLElement | null>; todayKey: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useActivityRefresh();
  const [step, setStep] = useState<Step>('what');
  const [name, setName] = useState('');
  const [what, setWhat] = useState('');
  const spanishPeople = useSpanishPeople();
  const [whatEs, setWhatEs] = useState('');
  const [kind, setKind] = useState<'weekly' | 'once'>('weekly');
  const [weekday, setWeekday] = useState(6);
  const [day, setDay] = useState(iso(addDaysToKey(todayKey, 1)));
  const [until, setUntil] = useState('');
  const [minutes, setMinutes] = useState(10 * 60);
  const [duration, setDuration] = useState(60);
  const [where, setWhere] = useState('');
  const [cap, setCap] = useState('1');
  const [services, setServices] = useState<ServiceKey[]>(['events']);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const n = STEPS.indexOf(step) + 1;
  const capN = Math.min(ACTIVITY_CAP_MAX, Math.max(1, Math.floor(Number(cap.replace(/\D/g, '')) || 0)));
  const dayKey = day.replace(/-/g, '');

  const go = (s: Step) => {
    setProblem(null);
    setStep(s);
  };
  const next = () => {
    if (step === 'what') {
      if (!name.trim()) return setProblem(t('activities.needName'));
      if (!what.trim()) return setProblem(t('activities.needWhat'));
      return go('when');
    }
    if (step === 'when') {
      if (kind === 'once' && !/^\d{8}$/.test(dayKey)) return setProblem(t('activities.needDate'));
      return go('where');
    }
    if (step === 'where') {
      if (!where.trim()) return setProblem(t('activities.needWhere'));
      if (!services.length) return setProblem(t('activities.needServices'));
      return go('confirm');
    }
  };
  const back = () => go(STEPS[Math.max(0, n - 2)]!);

  const whenWords =
    kind === 'weekly'
      ? t('activities.everyWeekday', { weekday: weekdayName(weekday, lang), time: minutesLabel(minutes), length: lengthWords(duration) })
      : t('activities.onDay', { day: /^\d{8}$/.test(dayKey) ? formatDayMedium(dayKey, lang) : '—', time: minutesLabel(minutes), length: lengthWords(duration) });

  const save = async () => {
    const body: ActivityInput = {
      name: name.trim(),
      what: what.trim(),
      whatEs: whatEs.trim(),
      where: where.trim(),
      schedule: kind === 'weekly' ? { kind: 'weekly', weekday, fromDayKey: todayKey, untilDayKey: until ? until.replace(/-/g, '') : null } : { kind: 'once', dayKey },
      minutes,
      duration,
      cap: capN,
      services,
    };
    setBusy(true);
    setProblem(null);
    try {
      const a = await postJson<ActivityView>('/api/activities/new', body);
      const m = t('activities.saved', { name: a.name });
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setProblem(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode = null;
  if (step === 'what') {
    body = (
      <>
        <TextField id="act-name" label={t('activities.name')} required value={name} onChange={setName} placeholder={t('activities.namePh')} maxLength={120} />
        <TextArea id="act-what" label={t('activities.what')} required value={what} onChange={setWhat} help={t('activities.whatHelp')} maxLength={1000} />
        <SpanishWording count={spanishPeople} value={whatEs} onChange={setWhatEs} rows={3} maxLength={1000} testId="act-what-es" />
      </>
    );
  } else if (step === 'when') {
    body = (
      <>
        <fieldset className="field">
          <legend className="eyebrow">{t('activities.howOften')}</legend>
          <div className="seg" role="group" aria-label={t('activities.howOften')}>
            <button type="button" aria-pressed={kind === 'weekly'} onClick={() => setKind('weekly')} data-testid="act-weekly">
              {t('activities.weekly')}
            </button>
            <button type="button" aria-pressed={kind === 'once'} onClick={() => setKind('once')} data-testid="act-once">
              {t('activities.once')}
            </button>
          </div>
        </fieldset>
        {kind === 'weekly' ? (
          <div className="grid-2">
            <SelectField id="act-weekday" label={t('activities.weekday')} value={String(weekday)} onChange={(v) => setWeekday(Number(v))} options={[0, 1, 2, 3, 4, 5, 6].map((d) => ({ value: String(d), label: weekdayName(d, lang) }))} />
            <TextField id="act-until" label={t('activities.until')} type="date" value={until} onChange={setUntil} help={t('activities.untilHelp')} />
          </div>
        ) : (
          <TextField id="act-date" label={t('activities.date')} type="date" required value={day} onChange={setDay} help={/^\d{8}$/.test(dayKey) ? weekdayName(dayOfWeek(dayKey), lang) : undefined} />
        )}
        <div className="grid-2">
          <SelectField id="act-time" label={t('activities.time')} value={String(minutes)} onChange={(v) => setMinutes(Number(v))} options={TIMES.map((m) => ({ value: String(m), label: minutesLabel(m) }))} />
          <SelectField id="act-length" label={t('activities.length')} value={String(duration)} onChange={(v) => setDuration(Number(v))} options={LENGTHS.map((m) => ({ value: String(m), label: lengthWords(m) }))} />
        </div>
        <p className="help">{t('activities.weekendsOpen')}</p>
      </>
    );
  } else if (step === 'where') {
    body = (
      <>
        <TextField id="act-where" label={t('activities.where')} required value={where} onChange={setWhere} placeholder={t('activities.wherePh')} maxLength={255} />
        <TextField id="act-cap" label={t('activities.cap')} required inputMode="numeric" value={cap} onChange={setCap} help={t('activities.capHelp', { max: ACTIVITY_CAP_MAX })} />
        <fieldset className="field">
          <legend className="eyebrow">{t('activities.services')}</legend>
          <div className="chips dense" role="group" aria-label={t('activities.services')}>
            {SERVICE_KEYS.map((s) => (
              <button key={s} type="button" className="chip multi" aria-pressed={services.includes(s)} onClick={() => setServices(services.includes(s) ? services.filter((x) => x !== s) : [...services, s])} data-testid={`act-service-${s}`}>
                {t(`vol.service.${s}`)}
              </button>
            ))}
          </div>
          <p className="help">{t('activities.servicesHelp')}</p>
        </fieldset>
        <p className="help">{t('activities.note')}</p>
      </>
    );
  } else {
    body = (
      <SummaryCard
        testId="act-confirm"
        rows={[
          { label: t('activities.c.name'), value: name.trim() },
          { label: t('activities.c.what'), value: what.trim() },
          { label: t('activities.c.when'), value: whenWords },
          { label: t('activities.c.where'), value: where.trim() },
          { label: t('activities.c.cap'), value: t('activities.capWords', { count: capN }) },
          { label: t('activities.c.services'), value: services.map((s) => t(`vol.service.${s}`)).join(', ') },
        ]}
      >
        <p className="help">{t('activities.note')}</p>
        <p className="help">{t('activities.saveNote')}</p>
      </SummaryCard>
    );
  }

  return (
    <SheetContent
      title={t('activities.new')}
      returnFocusTo={returnFocusTo}
      data-testid="activity-sheet"
      footer={
        <>
          {step === 'what' ? (
            <Button variant="secondary" type="button" onClick={onClose}>
              {t('activities.cancel')}
            </Button>
          ) : (
            <Button variant="secondary" type="button" onClick={back} data-testid="act-back">
              {t('activities.back')}
            </Button>
          )}
          {step === 'confirm' ? (
            <Button type="button" disabled={busy} onClick={() => save()} data-testid="act-save">
              {t('activities.save')}
            </Button>
          ) : (
            <Button type="button" onClick={next} data-testid="act-next">
              {t('activities.next')}
            </Button>
          )}
        </>
      }
    >
      <StepHead n={n} of={STEPS.length} question={t(`activities.q.${step}`)} id="act-q" testId="act-question" />
      {body}
      {problem ? (
        <p className="field-err" role="alert" data-testid="act-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
